/* eslint-disable @typescript-eslint/no-empty-function */
import { useRef, ReactNode } from "react"

import { BottomSheetHeights } from "@l2r-front/l2r-utils"

import * as Styled from "./ThresholdBottomSheet.styled"

const interactiveElements = [
    "puller-container",
    "treshold-bottom-sheet",
]

export function ThresholdBottomSheet(props: ThresholdBottomSheetProps) {
    const {
        disablePortal = false,
        height,
        onClose = () => { },
        setHeight,
        ...bottomSheetProps
    } = props

    const touchStartY = useRef<number | null>(null)
    const drawerStartHeight = useRef<number | null>(null)

    const isScrollable = (el: HTMLElement): boolean => {
        const { overflowY } = window.getComputedStyle(el)
        return ["auto", "scroll"].includes(overflowY) && el.scrollHeight > el.clientHeight
    }

    const findTouchedWithinSheet = (
        el: HTMLElement | null
    ): { isTouchedWithin: boolean; scrollableEl: null | { scrollTop: number; scrollHeight: number; clientHeight: number } } => {
        if (!el) {
            return { isTouchedWithin: false, scrollableEl: null }
        }

        const scrollableEl = isScrollable(el)
            ? {
                scrollTop: el.scrollTop,
                scrollHeight: el.scrollHeight,
                clientHeight: el.clientHeight,
            }
            : null

        if (interactiveElements.includes(el.id)) {
            return { isTouchedWithin: true, scrollableEl }
        }

        const result = findTouchedWithinSheet(el.parentElement)
        return {
            isTouchedWithin: result.isTouchedWithin,
            scrollableEl: result.scrollableEl || scrollableEl,
        }
    }

    const allowBottomSheetEvent = (event: TouchEvent, direction = 0): boolean => {
        const target = event.target as HTMLElement
        const { isTouchedWithin, scrollableEl } = findTouchedWithinSheet(target)

        if (!isTouchedWithin) return false
        if (!scrollableEl) return true

        const isAtTop = scrollableEl.scrollTop <= 0
        const isAtBottom = scrollableEl.scrollTop + scrollableEl.clientHeight >= scrollableEl.scrollHeight - 1

        return (direction > 0 && isAtTop) || (direction < 0 && isAtBottom)
    }

    const handleTouchStart = (event: React.TouchEvent) => {
        touchStartY.current = event.touches[0].clientY
        drawerStartHeight.current = height
    }

    const handleTouchMove = (event: React.TouchEvent) => {
        if (touchStartY.current === null || drawerStartHeight.current === null) return

        const touchY = event.touches[0].clientY
        const deltaY = touchStartY.current - touchY
        const direction = -deltaY

        if (!allowBottomSheetEvent(event.nativeEvent as TouchEvent, direction)) return

        const screenHeight = window.innerHeight
        let newHeight = drawerStartHeight.current + (deltaY / screenHeight) * 100
        newHeight = Math.max(BottomSheetHeights.CLOSED, Math.min(BottomSheetHeights.FULL, newHeight))

        setHeight(newHeight)
    }

    const handleTouchEnd = (event: React.TouchEvent) => {
        if (touchStartY.current === null || drawerStartHeight.current === null) return

        const touchY = event.changedTouches[0].clientY
        const deltaY = touchStartY.current - touchY
        const direction = -deltaY

        if (!allowBottomSheetEvent(event.nativeEvent as TouchEvent, direction)) return

        const screenHeight = window.innerHeight
        const estimatedHeight = drawerStartHeight.current + (deltaY / screenHeight) * 100

        const availableHeights = Object.entries(BottomSheetHeights)
            .filter(([_, value]) => value !== BottomSheetHeights.NONE) as [keyof typeof BottomSheetHeights, number][]

        const closestState = availableHeights.reduce<keyof typeof BottomSheetHeights>(
            (closestKey, [key, value]) => {
                const currentDiff = Math.abs(estimatedHeight - value)
                const closestDiff = Math.abs(estimatedHeight - BottomSheetHeights[closestKey])
                return currentDiff < closestDiff ? key : closestKey
            },
            "CLOSED"
        )

        setHeight(BottomSheetHeights[closestState])
        touchStartY.current = null
        drawerStartHeight.current = null
    }

    return (
        <Styled.BottomSheet
            {...bottomSheetProps}
            id="treshold-bottom-sheet"
            open
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            displayPuller
            height={height}
            hideBackdrop
            onClose={onClose}
            onOpen={() => { }}
            allowSwipeInChildren={false}
            disableDiscovery
            disableBackdropTransition
            disableSwipeToOpen
            disablePortal={disablePortal}
            disableEnforceFocus
        />
    )
}

export type ThresholdBottomSheetProps = {
    className?: string
    children?: ReactNode
    disablePortal?: boolean
    height: number
    onClose?: () => void
    setHeight: (_value: number) => void
}